import {Helmet} from 'react-helmet';
import {toast} from 'react-toastify';
import { useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import React, {useEffect, useRef, useState} from 'react';
import DashboardHeader from '../../components/DashboardHeader';
import FacilityModal from '../../components/FacilityModal';
import Input from '../../components/Input';
import Wrapper from '../../components/Wrapper';
import {
  useLazyGetFacilityIndexQuery,
  useRemoveFacilityMutation,
  useUnAssignFacilityMutation,
} from '../../store/builder/inspections.api';
import {useErrorHandler} from '../../global/ErrorsHandler';
import CustomPaginate from '../../components/PaginateComponent';
import {useDebounce} from '../LogSheets/hooks';
import useLoader from '../../global/UseLoadingHandler';
import Pencil from "../../SVG/Pencil";
import Trash from "../../SVG/Trash";
import Copy from "../../SVG/Copy";
import {copyText} from "../../global/utils";
import useClickOutside from "../../hooks/useClickOutside";
import './index.scss';
import ToggleDropdown from "../../components/ToogleDropdown";
import {useTranslation} from '../../hooks/useTranslation';

export default function Facility() {
  const {t} = useTranslation();
  const dropdownRef = useRef(null)
  ;
  const navigate = useNavigate();
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const [modal, setModal] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [unAssignStatus, setUnAssignStatus] = useState(false);
  const [chosenFacility, setChosenFacility] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [lastElem, setLastElem] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(false);
  const [isOpenDropDown, setIsOpenDropDown] = useState(false);
  const debouncedSearchValue = useDebounce(search, 500);

  const [
    getFacility,
    {
      data: facilityData,
      error: facilityError,
      isFetching: facilityIsLoading,
    },
  ] = useLazyGetFacilityIndexQuery();

  const [
    removeFacility,
    {
      error: removeFacilityError,
      isFetching: removeFacilityIsLoading,
    },
  ] = useRemoveFacilityMutation();

  const [
    unAssign,
    {
      error: unAssignError,
      isFetching: unAssignIsLoading,
    },
  ] = useUnAssignFacilityMutation();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    facilityError,
    removeFacilityError,
    unAssignError,
  ]);
  useLoader([facilityIsLoading, unAssignIsLoading, removeFacilityIsLoading]);
  useEffect(() => {
    if (modal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [modal]);

  useEffect(() => {
    getFacility({page: currentPage, search: debouncedSearchValue});
  }, [currentPage, debouncedSearchValue]);


  useClickOutside(dropdownRef, () => {
    setIsOpenDropDown(false);
    setSelectedFacility(null);
  });

  const handleSearch = e => {
    setSearch(e.target.value);
  };

  const handlePageClick = page => {
    setCurrentPage(page.selected + 1);
    window.scrollTo(0, 0);
  };

  const getData = () => {
    getFacility({page: currentPage});
  };

  const handleDropdownToggle = (id) => {
    if (selectedFacility === id) {
      setIsOpenDropDown(!isOpenDropDown);
    } else {
      setSelectedFacility(id);
      setIsOpenDropDown(true);
    }
  };

  const renderUnAssignModal = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('facility.unassignModal.title')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setUnAssignStatus(false)}
            />
          </div>
          <div
            className='modal__content-body'
            style={{display: 'flex', justifyContent: 'center'}}
          >
            {t('facility.unassignModal.message')}
          </div>
          <div className='modal__content-bottom'>
            <button
              className='close'
              onClick={() => {
                setUnAssignStatus(false);
              }}
            >
              {t('facility.unassignModal.cancel')}
            </button>
            <button
              className='save danger '
              onClick={() => {
                unAssign(chosenFacility)
                  .unwrap()
                  .then(res => {
                    toast.success(
                      res?.message ? res?.message : res?.data?.message,
                    );
                    setUnAssignStatus(false);
                    getFacility({page: currentPage});
                  });
              }}
            >
              {t('facility.unassignModal.unassign')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const renderDeleteModal = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('facility.deleteModal.title')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setDeleteModal(false)}
            />
          </div>
          <div
            className='modal__content-body'
            style={{display: 'flex', justifyContent: 'center'}}
          >
            {t('facility.deleteModal.message')}
          </div>
          <div className='modal__content-bottom'>
            <button
              className='save danger '
              onClick={() => {
                removeFacility(chosenFacility)
                  .then(res => {
                    toast.success(res.data.message);
                    setDeleteModal(false);
                    setChosenFacility(null);
                    getFacility(currentPage);
                  })
                  .catch(() => {
                    setDeleteModal(false);
                  });
              }}
            >
              {t('facility.deleteModal.delete')}
            </button>
            <button
              className='close almost'
              onClick={() => setDeleteModal(false)}
            >
              {t('facility.deleteModal.cancel')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('facility.pageTitle')}</title>
      </Helmet>
      {modal && <FacilityModal setModal={setModal} getData={getData} />}
      {unAssignStatus && renderUnAssignModal()}
      {deleteModal && renderDeleteModal()}
      <DashboardHeader
        title={t('facility.title')}
        showBtn={true}
        type='facility'
        action={() => setModal(true)}
      />
      <div className='facility'>
        <div className='facility__content'>
          <div className='facility__search'>
            <Input
              type='text'
              placeholder={t('facility.searchPlaceholder')}
              name='facilitySearch'
              id='facilitySearch'
              image={'/assets/icons/search.svg'}
              maxwidth={264}
              onChange={handleSearch}
            />
          </div>
          <div className='facility__table'>
            <div className='facility__table-header'>
              <div className='facility__th'>{t('facility.tableHeaders.facilityName')}</div>
              <div className='facility__th'>{t('facility.tableHeaders.totalInspections')}</div>
              <div className='facility__th'>{t('facility.tableHeaders.inspectionsEnabled')}</div>
              <div className='facility__th'>{t('facility.tableHeaders.customInspectionsEnabled')}</div>
              <div className='facility__th'>{t('facility.tableHeaders.nfc')}</div>
              {/*<div className="facility__th">Last annual date</div>*/}
              <div className='facility__th'></div>
            </div>
            {facilityData?.data?.length > 0 ? (
              <div className='facility__table-body'>
                {facilityData?.data?.map((item, index) => (
                    <div className='facility__row' key={index}>
                      <div className='facility__td'>{item?.name}</div>
                      <div className='facility__td'>{item?.links?.length}</div>
                      <div className='facility__td'>
                        {item?.inspections_enabled ? t('facility.status.enabled') : t('facility.status.notEnabled')}
                      </div>
                      <div className='facility__td'>
                        {item?.custom_inspections_enabled ? t('facility.status.enabled') : t('facility.status.notEnabled')}
                      </div>
                      <div className='facility__td'>
                        <p className='equipment__table-title'>{t('facility.tableHeaders.nfc')}</p>
                        {item?.beacon_address ? (
                            <p className='equipment__table-subtitle'>
                              <img src='/assets/icons/nfc-active.png' alt='icon'/>
                              <img
                                  src='/assets/icons/beacon-address.svg'
                                  onClick={() => {
                                    setUnAssignStatus(true);
                                    setChosenFacility(item.id);
                                  }}
                                  alt='icon'
                              />
                            </p>
                        ) : (
                            <p className='equipment__table-subtitle'>
                              <img
                                  src='/assets/icons/nfc-inactive.png'
                                  alt='icon'
                              />
                            </p>
                        )}
                      </div>
                      <ToggleDropdown
                          index={index}
                          itemId={item.id}
                          showNdxId={userInfo?.is_global}
                          lengthOfData={facilityData?.data?.length}
                          selectedItem={selectedFacility}
                          setSelectedItem={setSelectedFacility}
                      >
                        <div
                            className={'blue'}
                            onClick={() => navigate('/facility/edit/' + item.id)}
                        >
                          <Pencil color={'#999999'}/>
                          {t('facility.actions.edit')}

                        </div>
                        {userInfo?.is_global && (
                            <div
                                className={'red'}
                                onClick={() => {
                                  setDeleteModal(true);
                                  setChosenFacility(item?.id);
                                }}
                            >
                              <Trash color={'#999999'}/>
                              {t('facility.actions.delete')}
                            </div>
                        )}
                      </ToggleDropdown>
                    </div>
                ))}
              </div>
            ) : (
                <div className='facility__table-empty'>
                  <img
                      src='/assets/icons/facility-empty.svg'
                      alt='empty'
                      onClick={() => {
                        setModal(true);
                      }}
                  />
                  <p>{t('facility.messages.noFacilities')}</p>
                </div>
            )}
          </div>
          <CustomPaginate
              pageCount={facilityData?.pages}
              handlePageClick={handlePageClick}
          />
        </div>
      </div>
    </Wrapper>
  );
}
